import React, { useMemo } from "react"

import {
  Button,
  CircularProgress,
  Dialog,
  Divider,
  Stack,
} from "@mui/material"

import toast from "react-hot-toast"

import {
  MeetingStatus,
  Meeting,
  MeetingJoinType,
  Organisation,
  User,
} from "../../../types.tsx"
import {
  canUserEditMeeting,
  getMeetingStatus,
} from "../../../types-helpers.tsx"
import {
  useGetCurrentUser,
  useGetMeeting,
  useGetPatient,
  useCancelMeeting,
  useListOrganisations,
  useListUsers,
} from "../../../store.tsx"

import AttendeeList from "../../attendees/attendee-list"
import { getAttendeeStatus } from "../../attendees/utils"
import MeetingStatusDisplay from "./meeting-status.tsx"
import PatientChip from "../../chips/patient-chip.tsx"
import TimeSummaryTag from "../common/time-summary-tag.tsx"

interface MeetingInfoModalProps {
  open: boolean
  handleClose: () => void
  meetingId: string
  disableEdit?: boolean
  onEditMeeting?: (meeting: Meeting) => void
}


const MeetingInfoModal: React.FC<MeetingInfoModalProps> = ({
  open,
  handleClose,
  meetingId,
  disableEdit,
  onEditMeeting,
}: MeetingInfoModalProps) => {

  // Always fetch meeting data to ensure we have the latest info after updates
  const { data: meeting = null, isLoading: isLoadingMeeting } = useGetMeeting(meetingId);

  const { data: patient, isLoading: isLoadingPatient } = useGetPatient(meeting?.patientId || "");
  const { data: users } = useListUsers();
  const { data: currentUser } = useGetCurrentUser();
  const { data: teams } = useListOrganisations();

  const meetingDuration = useMemo(() => {
    if (!meeting?.startTime) {
      return undefined;
    }

    const startTime = new Date(meeting.startTime);
    const endTime = meeting.endTime ? new Date(meeting.endTime) : new Date(startTime.getTime() + 60 * 60000);

    return Math.round((endTime.getTime() - startTime.getTime()) / 60000);
  }, [meeting]);

  const { canEdit } = meeting ? canUserEditMeeting(meeting, currentUser) : { canEdit: false }
  const showEdit = canEdit && !disableEdit
  const meetingStatus: MeetingStatus = meeting ? getMeetingStatus(meeting).status : MeetingStatus.Scheduled

  const selectedUsers = useMemo(() => {
    if (!meeting || !users) {
      return [];
    }

    return meeting.attendeeIds
      .map((userId) => users.find((user) => user.userId === userId))
      .filter((user): user is User => !!user)
      .map((user) => ({
        userId: user.userId,
        label: user.fullName || user.email || "Unknown user",
        status: getAttendeeStatus(
          meetingStatus,
          meeting.joinedInfo?.find((joinInfo) => joinInfo.userId === user.userId),
        ),
        teams: (user.organisationIds || [])
          .map((teamId) => teams?.find((team) => team.organisationId === teamId))
          .filter((team): team is Organisation => !!team)
          .map((team) => ({
            teamId: team.organisationId,
            name: team.name,
            color: team.color,
          })),
      }));
  }, [meeting, meetingStatus, teams, users]);

  const selectedTeams = useMemo(() => {
    if (!meeting || !teams) {
      return [];
    }

    return meeting.organisationIds
      .map((teamId) => teams.find((team) => team.organisationId === teamId))
      .filter((team): team is Organisation => !!team)
      .map((team) => ({
        teamId: team.organisationId,
        name: team.name,
        color: team.color,
      }));
  }, [meeting, teams]);

  const deleteSuccessToast = () => {
    toast.success("Meeting cancelled successfully")
    onClose()
  }

  const deleteFailureToast = () => {
    toast.error("Meeting cancellation failed")
  }

  const { mutate: cancelMeeting } = useCancelMeeting(deleteSuccessToast, deleteFailureToast)

  const handleCancelMeeting = () => {
    if (!meeting) {
      console.error("No meeting!")
      onClose()
      return
    }

    cancelMeeting({ meetingId: meeting.meetingId })
  }

  const handleEdit = () => {
    onClose()
    if (onEditMeeting && meeting) {
      onEditMeeting(meeting)
    }
  }

  const onClose = () => {
    handleClose()
  }

  if (!meeting) return null

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <Stack padding={2} spacing={2}>
        <Stack spacing={1.5}>
          <Stack direction={"row"} spacing={1} alignItems={"center"} justifyContent={"space-between"}>
            {isLoadingPatient ? (
              <CircularProgress size={16} />
            ) : (
              <PatientChip label={meeting.patientId ? patient?.displayId || "" : ""} />
            )}
            <TimeSummaryTag
              startTimes={meeting.startTime ? [new Date(meeting.startTime)] : []}
              duration={meetingDuration}
            />
          </Stack>

          <Divider orientation="horizontal" flexItem />

          <MeetingStatusDisplay
            meeting={meeting}
            showEdit={showEdit}
            onEdit={handleEdit}
            onCancelMeeting={handleCancelMeeting}
          />

          <Divider orientation="horizontal" flexItem />

          <AttendeeList
            selectedUsers={selectedUsers}
            selectedInviteEmails={[]}
            selectedTeams={selectedTeams}
            editable={false}
            split={true}
          />
        </Stack>

        <Button
          variant="outlined"
          fullWidth
          onClick={onClose}
        >
          Close
        </Button>
      </Stack>
    </Dialog>
  )
}

export default MeetingInfoModal
